import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmDialogList from '../../../../../components/ConfirmDialogList';
import configSiteUrls from '../../../configs/configSiteUrls';
import { ISouvidoria, ISouvidoriaList, Ouvidoria, OuvidoriaList } from '../types/Ouvidoria';
import { useDeleteOuvidoriaMutation, useGetOuvidoriasQuery } from '../ouvidoriaApiSlice';
import { OuvidoriaDataTable } from './OuvidoriaDataTable';

export const OuvidoriaListForm = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [ouvidorias, setOuvidorias] = useState<OuvidoriaList[]>([]);

  const [ouvidoriaToDelete, setOuvidoriaToDelete] = useState<OuvidoriaList>(ISouvidoriaList);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);
  const [deleteOuvidoria, { error: errorOuvidoria, isLoading, isSuccess }] = useDeleteOuvidoriaMutation();

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    // Limpar o timeout anterior
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Configurar o timeout para o debounce
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700); // Tempo de debounce (700ms)

    // Limpar o timeout quando o componente for desmontado ou o efeito for reiniciado
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]); // Dependência do useEffect: quando o 'search' mudar

  const { data, isFetching, error } = useGetOuvidoriasQuery({
    search: debouncedSearch
  });

  useEffect(() => {
    if (data) {
      setOuvidorias(data);
    }
    if (error) {
      enqueueSnackbar('Registros  não encontrados!', { variant: 'error' });
    } else if (isFetching) {
      enqueueSnackbar('Buscando registros...', { variant: 'info' });
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [data, error, isFetching, enqueueSnackbar]);

  const handleExcluirtClick = (rowData: any) => {
    setOuvidoriaToDelete(rowData);
    setOpenConfirmExcluir(true);
  };
  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };
  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteOuvidoria({
      id: ouvidoriaToDelete.id ? ouvidoriaToDelete.id : ''
    });
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro arquivado com sucesso!', { variant: 'success' });
      // fetchDados();
    }
    if (errorOuvidoria) {
      enqueueSnackbar('Registro não arquivado!', { variant: 'error' });
    }
  }, [isSuccess, errorOuvidoria]);

  if (isFetching || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={[
          `Você tem certeza de que deseja ${ouvidoriaToDelete.arquivado ? 'desarquivar' : 'arquivar'} essa Ouvidoria?`,
          ouvidoriaToDelete.categoria ? `Categoria: ${ouvidoriaToDelete.categoria}` : ''
        ]}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />
      <Paper sx={{ width: '100%', height: '100vh', padding: '12px' }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Registro de Ouvidorias
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChangeSearch} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>
        </Grid>

        <OuvidoriaDataTable ouvidorias={ouvidorias} handleExcluirtClick={handleExcluirtClick} baseEditUrl={configSiteUrls.SITE_OUVIDORIA_EDIT_LK} />
      </Paper>
    </>
  );
};
